import { action, computed, observable } from 'mobx'
import { catchError, combineLatest, EMPTY } from 'rxjs'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { GetCurrentMemberFn, SearchUsersFn } from '@/features/iam'
import type { GetAdvisorUserPublicIdFn } from '@/features/client-user-management'
import { SingleSelectFilter } from '@/infra/SingleSelectFilter'
import {
  FilterItemSource,
  type FilterStorageFactory,
  FiltersViewModel,
} from '@/infra/FiltersViewModel'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { switchMap } from 'rxjs/operators'
import { Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { tapAction } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

interface ClientUserFilter {
  inactive?: boolean | null
}

type ClientUserSearchFilter = ClientUserFilter & { searchText?: string }

/**
 * Options for {@link ClientUserSearchViewModel}.
 */
export interface ClientUserSearchOptions {
  /**
   * Namespace the filters are stored under.
   */
  filterStorageNamespace: string
  /**
   * Only lists the clients assigned to this advisor.
   */
  getAdvisorUserPublicId?: GetAdvisorUserPublicIdFn
}

export class ClientUserSearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly searchText = new SearchTextViewModel(500)

  readonly filterStorage

  @observable
  accessor totalUsers = 0

  @observable
  accessor workspaceId: Id = Id.none

  readonly filters

  readonly scrollProvider = new InfiniteScrollProvider<
    SearchableUser,
    ClientUserSearchFilter
  >({
    limit: 100,
    fetchItems: ({ paging, filter, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading clients failed',
        signal,
      )
      return withRetry(() =>
        this._searchUsers(
          {
            searchText: filter?.searchText,
            limit: paging.limit,
            after: paging.after,
            workspaceId: this.workspaceId,
            userTypes: ['User'],
            inactive: filter?.inactive,
            advisorUserPublicId: this._options.getAdvisorUserPublicId?.(),
          },
          signal,
        ),
      )
    },
  })

  private readonly _query$

  constructor(
    private readonly _searchUsers: SearchUsersFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _createFilterStorage: FilterStorageFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _options: ClientUserSearchOptions,
  ) {
    this.filterStorage = this._createFilterStorage(
      this._options.filterStorageNamespace,
    )

    this.filters = new FiltersViewModel<ClientUserFilter>({
      filterStorage: this.filterStorage,
      definitions: {
        inactive: {
          icon: 'pending',
          title: 'Inactive',
          implicit: true,
          factory: (key, title) =>
            new SingleSelectFilter(
              title,
              key,
              false,
              FilterItemSource.static([]),
            ),
        },
      },
    })

    this._query$ = combineLatest([
      this.filters.appliedFilters$,
      this.searchText.debouncedValue$,
    ]).pipe(
      switchMap(([appliedFilters, searchText]) =>
        withAbortSignal((signal) =>
          this.scrollProvider.fetch({
            filter: { searchText, ...appliedFilters },
            signal,
          }),
        ).pipe(
          // The retry alert has reported the failure.
          catchError(() => EMPTY),
        ),
      ),
      tapAction((result) => (this.totalUsers = result.total ?? 0)),
    )

    this._lifecycle.manage(this.filters).manage(this._query$)
  }

  @computed
  get isFetchingInitial() {
    return (
      this.scrollProvider.fetch.pending && !this.scrollProvider.isRevalidating
    )
  }

  /**
   * Whether a search text or filter is applied.
   */
  @computed
  get isSearchFilterApplied() {
    const isSearching = this.searchText.value !== ''
    const haveFiltersApplied = this.filters.appliedFilterCount > 0

    return isSearching || haveFiltersApplied
  }

  /**
   * Whether the search finished without finding any clients.
   */
  @computed
  get shouldShowEmptyState() {
    return this.scrollProvider.isDone && this.users.length === 0
  }

  /**
   * The clients loaded so far.
   */
  @computed
  get users() {
    return this.scrollProvider.items
  }

  @computed
  get userInactiveFilter() {
    return this.filters.getFilter('inactive') as SingleSelectFilter<
      string,
      boolean
    >
  }

  activate() {
    const currentSessionWorkspace =
      this._getCurrentMember().workspace ?? Workspace.none
    this.workspaceId = currentSessionWorkspace.id
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }
}
